import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CircularProgress } from "@mui/material";
import {
  FiChevronLeft, FiChevronRight, FiDownload, FiExternalLink, FiFile, FiMaximize, FiRefreshCw, FiRotateCw, FiX, FiZoomIn, FiZoomOut,
} from "react-icons/fi";
import AppTooltip from "../AppTooltip";
import { showErrorToast } from "../../../constants/ToastService.js";
import { displayName, downloadFile, fetchBlob, fileMeta, fileType, parseCsv } from "./fileTypes";
import "./FileViewer.css";

const CSV_PREVIEW_ROWS = 500;
const ZOOM_STEPS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2, 3, 4];

/* Full-screen viewer for a ticket's files, Teams-style: the current file in the
   middle, ‹ › (or ← →) through `files`, and a strip of every file below.
   files: [{ key, name, by, time, source, thumbUrl, getUrl: () => Promise<url> }] */
const FileViewer = ({ files, index, onIndexChange, onClose }) => {
  const file = files[index];
  const fileRef = useRef(file);
  fileRef.current = file;
  const cache = useRef(new Map()); // key → { blobUrl, csv }
  const imgRef = useRef(null);
  const stripRef = useRef(null);
  const [view, setView] = useState({ status: "loading" }); // loading | ready | error
  const [zoom, setZoom] = useState(null); // null = fit to screen
  const [rotate, setRotate] = useState(0);
  const [attempt, setAttempt] = useState(0);
  const fileKey = file?.key;
  const type = fileType(file?.name);

  // Blobs live until the viewer closes; delayed so a tab opened from one can load.
  useEffect(() => {
    const blobs = cache.current;
    return () => blobs.forEach(v => v.blobUrl && setTimeout(() => URL.revokeObjectURL(v.blobUrl), 60000));
  }, []);

  useEffect(() => {
    const f = fileRef.current;
    if (!f) return undefined;
    setZoom(null);
    setRotate(0);
    const t = fileType(f.name);
    if (t.kind === "other") { setView({ status: "ready" }); return undefined; }
    const hit = cache.current.get(f.key);
    if (hit) { setView({ status: "ready", ...hit }); return undefined; }
    let live = true;
    setView({ status: "loading" });
    (async () => {
      try {
        const blob = await fetchBlob(await f.getUrl(), f.name);
        const entry = { blobUrl: URL.createObjectURL(blob) };
        if (t.kind === "csv") entry.csv = parseCsv(await blob.text());
        cache.current.set(f.key, entry);
        if (live) setView({ status: "ready", ...entry });
      } catch (e) {
        if (live) setView({ status: "error", message: e?.message || "Could not load the file" });
      }
    })();
    return () => { live = false; };
  }, [fileKey, attempt]);

  const go = useCallback((step) => {
    const next = index + step;
    if (next >= 0 && next < files.length) onIndexChange(next);
  }, [index, files.length, onIndexChange]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, onClose]);

  // Keep the current tile in view in the strip.
  useEffect(() => {
    stripRef.current?.querySelector(".fv-tile--active")?.scrollIntoView({ block: "nearest", inline: "center" });
  }, [index]);

  if (!file) return null;

  // Zooming starts from whatever "fit" currently shows.
  const currentScale = () => {
    if (zoom) return zoom;
    const img = imgRef.current;
    return img && img.naturalWidth ? img.clientWidth / img.naturalWidth : 1;
  };
  const zoomBy = (dir) => {
    const cur = currentScale();
    const next = dir > 0 ? ZOOM_STEPS.find(z => z > cur + 0.01) : [...ZOOM_STEPS].reverse().find(z => z < cur - 0.01);
    if (next) setZoom(next);
  };

  const handleDownload = async () => {
    try { await downloadFile(await file.getUrl(), file.name); }
    catch { showErrorToast("Download failed"); }
  };

  const retry = () => { cache.current.delete(file.key); setAttempt(a => a + 1); };

  const renderBody = () => {
    if (view.status === "loading") return <CircularProgress size={34} sx={{ color: "#fff" }} />;
    if (view.status === "error") {
      return (
        <div className="fv-empty">
          <FiFile size={40} />
          <p>{view.message}</p>
          <button className="fv-btn fv-btn--solid" onClick={retry}><FiRefreshCw size={13} /> Retry</button>
        </div>
      );
    }
    if (type.kind === "image") {
      const style = zoom
        ? { width: (imgRef.current?.naturalWidth || 0) * zoom || undefined, maxWidth: "none", maxHeight: "none", transform: `rotate(${rotate}deg)` }
        : { transform: `rotate(${rotate}deg)` };
      return <img ref={imgRef} className={`fv-image${zoom ? " fv-image--zoomed" : ""}`} src={view.blobUrl} alt={displayName(file.name)} style={style} />;
    }
    if (type.kind === "pdf") return <iframe className="fv-pdf" src={view.blobUrl} title={displayName(file.name)} />;
    if (type.kind === "csv") {
      const rows = view.csv || [];
      const [head = [], ...body] = rows;
      return (
        <div className="fv-csv">
          <table>
            <thead><tr>{head.map((h, i) => <th key={i}>{h}</th>)}</tr></thead>
            <tbody>
              {body.slice(0, CSV_PREVIEW_ROWS).map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j}>{c}</td>)}</tr>)}
            </tbody>
          </table>
          {body.length > CSV_PREVIEW_ROWS && <p className="fv-csv__more">Showing the first {CSV_PREVIEW_ROWS} of {body.length} rows. Download to see all.</p>}
        </div>
      );
    }
    return (
      <div className="fv-empty">
        <span className="fv-empty__badge" style={{ background: type.color }}>{type.label}</span>
        <p>Preview isn't available for {type.label} files.</p>
        <button className="fv-btn fv-btn--solid" onClick={handleDownload}><FiDownload size={13} /> Download</button>
      </div>
    );
  };

  const onWheel = (e) => {
    if (type.kind !== "image" || view.status !== "ready") return;
    zoomBy(e.deltaY < 0 ? 1 : -1);
  };

  return createPortal(
    <div className="fv-overlay" role="dialog" aria-modal="true" aria-label={displayName(file.name)}>
      <div className="fv-header">
        <span className="fv-type" style={{ background: type.color }}>{type.label}</span>
        <div className="fv-title">
          <span className="fv-title__name">{displayName(file.name)}</span>
          <span className="fv-title__meta">{[fileMeta(file.by, file.time), file.source].filter(Boolean).join(" · ")}</span>
        </div>
        <span className="fv-count">{index + 1} / {files.length}</span>
        <AppTooltip title="Download"><button className="fv-btn" onClick={handleDownload} aria-label="Download"><FiDownload size={16} /></button></AppTooltip>
        {type.kind !== "other" && (
          <AppTooltip title="Open in new tab">
            <span>
              <button className="fv-btn" disabled={view.status !== "ready"} onClick={() => window.open(view.blobUrl, "_blank")} aria-label="Open in new tab"><FiExternalLink size={16} /></button>
            </span>
          </AppTooltip>
        )}
        <AppTooltip title="Close (Esc)"><button className="fv-btn" onClick={onClose} aria-label="Close"><FiX size={18} /></button></AppTooltip>
      </div>

      <div className="fv-stage" onWheel={onWheel} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
        {index > 0 && <button className="fv-nav fv-nav--prev" onClick={() => go(-1)} aria-label="Previous file"><FiChevronLeft size={26} /></button>}
        <div className={`fv-body fv-body--${type.kind}${zoom ? " fv-body--zoomed" : ""}`}>{renderBody()}</div>
        {index < files.length - 1 && <button className="fv-nav fv-nav--next" onClick={() => go(1)} aria-label="Next file"><FiChevronRight size={26} /></button>}
        {type.kind === "image" && view.status === "ready" && (
          <div className="fv-tools">
            <AppTooltip title="Zoom out"><button className="fv-btn" onClick={() => zoomBy(-1)} aria-label="Zoom out"><FiZoomOut size={15} /></button></AppTooltip>
            <span className="fv-tools__pct">{zoom ? `${Math.round(zoom * 100)}%` : "Fit"}</span>
            <AppTooltip title="Zoom in"><button className="fv-btn" onClick={() => zoomBy(1)} aria-label="Zoom in"><FiZoomIn size={15} /></button></AppTooltip>
            <AppTooltip title="Fit to screen"><button className="fv-btn" onClick={() => setZoom(null)} aria-label="Fit to screen"><FiMaximize size={15} /></button></AppTooltip>
            <AppTooltip title="Rotate"><button className="fv-btn" onClick={() => setRotate(r => (r + 90) % 360)} aria-label="Rotate"><FiRotateCw size={15} /></button></AppTooltip>
          </div>
        )}
      </div>

      {files.length > 1 && (
        <div className="fv-strip" ref={stripRef}>
          {files.map((f, i) => {
            const t = fileType(f.name);
            return (
              <AppTooltip key={f.key} title={displayName(f.name)}>
                <button className={`fv-tile${i === index ? " fv-tile--active" : ""}`} onClick={() => onIndexChange(i)} aria-label={displayName(f.name)}>
                  {t.kind === "image" && f.thumbUrl
                    ? <img src={f.thumbUrl} alt="" onError={e => { e.currentTarget.style.display = "none"; }} />
                    : <span className="fv-tile__badge" style={{ background: t.color }}>{t.label}</span>}
                </button>
              </AppTooltip>
            );
          })}
        </div>
      )}
    </div>,
    document.body
  );
};

export default FileViewer;
